import { useState, useEffect, useMemo } from "react";
import { BarChart3, TrendingUp, Users, DollarSign, Loader2 } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { startOfMonth, endOfMonth, subMonths, format } from "date-fns";

interface ClientRow {
  id: string;
  company_name: string;
  package: string;
}

interface DemoRow {
  id: string;
  client_id: string;
  status: string;
  outcome: string | null;
  deal_value: number | null;
  demo_date: string;
}

interface ClientMetrics {
  clientId: string;
  clientName: string;
  package: string;
  demosBooked: number;
  demosShowed: number;
  showRate: number;
  demosClosed: number;
  closeRate: number;
  avgDealSize: number;
  totalRevenue: number;
}

export function DemoMetricsTab() {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [demos, setDemos] = useState<DemoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('current');

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const getDateRange = () => {
    const now = new Date();
    let target: Date;
    if (selectedMonth === 'last') {
      target = subMonths(now, 1);
    } else if (selectedMonth === '2months') {
      target = subMonths(now, 2);
    } else {
      target = now;
    }
    return {
      start: format(startOfMonth(target), 'yyyy-MM-dd'),
      end: format(endOfMonth(target), 'yyyy-MM-dd'),
    };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();

      // Fetch clients and demos in parallel
      const [clientsRes, demosRes] = await Promise.all([
        supabase
          .from('clients')
          .select('id, company_name, package')
          .eq('status', 'active')
          .order('company_name'),
        supabase
          .from('demos')
          .select('id, client_id, status, outcome, deal_value, demo_date')
          .gte('demo_date', start)
          .lte('demo_date', end),
      ]);

      if (clientsRes.error) throw clientsRes.error;
      if (demosRes.error) throw demosRes.error;

      setClients(clientsRes.data || []);
      setDemos(demosRes.data || []);
    } catch (error) {
      console.error('Error loading demo metrics:', error);
      toast.error('Failed to load demo metrics');
    } finally {
      setLoading(false);
    }
  };

  const clientMetrics: ClientMetrics[] = useMemo(() => {
    const clientDemoMap: Record<string, DemoRow[]> = {};

    demos.forEach(demo => {
      if (!clientDemoMap[demo.client_id]) {
        clientDemoMap[demo.client_id] = [];
      }
      clientDemoMap[demo.client_id].push(demo);
    });

    return clients.map(client => {
      const clientDemos = clientDemoMap[client.id] || [];
      const demosBooked = clientDemos.length;
      const demosShowed = clientDemos.filter(d => d.status === 'completed' || d.status === 'no_show').length;
      const demosClosed = clientDemos.filter(d => d.outcome === 'closed').length;
      const closedDeals = clientDemos.filter(d => d.outcome === 'closed' && d.deal_value);
      const totalRevenue = closedDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0);
      const avgDealSize = closedDeals.length > 0 ? totalRevenue / closedDeals.length : 0;

      return {
        clientId: client.id,
        clientName: client.company_name,
        package: client.package || 'standard',
        demosBooked,
        demosShowed,
        showRate: demosBooked > 0 ? (demosShowed / demosBooked * 100) : 0,
        demosClosed,
        closeRate: demosShowed > 0 ? (demosClosed / demosShowed * 100) : 0,
        avgDealSize: Math.round(avgDealSize),
        totalRevenue
      };
    });
  }, [clients, demos]);

  const totals = useMemo(() => {
    return clientMetrics.reduce((acc, m) => ({
      demosBooked: acc.demosBooked + m.demosBooked,
      demosShowed: acc.demosShowed + m.demosShowed,
      demosClosed: acc.demosClosed + m.demosClosed,
      totalRevenue: acc.totalRevenue + m.totalRevenue,
    }), { demosBooked: 0, demosShowed: 0, demosClosed: 0, totalRevenue: 0 });
  }, [clientMetrics]);

  const avgShowRate = totals.demosBooked > 0 ? (totals.demosShowed / totals.demosBooked * 100).toFixed(1) : '0';
  const avgCloseRate = totals.demosShowed > 0 ? (totals.demosClosed / totals.demosShowed * 100).toFixed(1) : '0';

  const chartData = clientMetrics
    .filter(m => m.demosBooked > 0)
    .map(m => ({
      name: m.clientName,
      closeRate: m.closeRate,
      fill: m.closeRate >= 30 ? 'hsl(var(--success))' : m.closeRate >= 20 ? 'hsl(var(--secondary))' : 'hsl(var(--destructive))'
    }));

  const getMonthLabel = () => {
    const now = new Date();
    if (selectedMonth === 'last') return format(subMonths(now, 1), 'MMMM yyyy');
    if (selectedMonth === '2months') return format(subMonths(now, 2), 'MMMM yyyy');
    return 'This Month';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Month Selector */}
      <div className="flex justify-end">
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">This Month</SelectItem>
            <SelectItem value="last">Last Month</SelectItem>
            <SelectItem value="2months">2 Months Ago</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-secondary/20">
              <BarChart3 className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="text-3xl font-bold">{totals.demosBooked}</p>
              <p className="text-sm text-muted-foreground">Total Demos</p>
            </div>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-success/20">
              <TrendingUp className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-3xl font-bold">{avgShowRate}%</p>
              <p className="text-sm text-muted-foreground">Show Rate</p>
            </div>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/20">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-3xl font-bold">{avgCloseRate}%</p>
              <p className="text-sm text-muted-foreground">Close Rate</p>
            </div>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-success/20">
              <DollarSign className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-3xl font-bold">${totals.totalRevenue.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Client Metrics Table */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Demo Performance by Client ({getMonthLabel()})</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {clientMetrics.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="font-medium mb-1">No demo data for this period</p>
              <p className="text-sm text-muted-foreground">Demos will appear here once they are recorded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Package</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Demos Booked</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Demos Showed</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Show Rate</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Demos Closed</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Close Rate</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Avg Deal Size</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {clientMetrics.map((m) => (
                    <tr key={m.clientId} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4 font-medium">{m.clientName}</td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={m.package === 'elite' ? 'warning' : 'info'}>
                          {m.package}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4 text-center">{m.demosBooked}</td>
                      <td className="py-3 px-4 text-center">{m.demosShowed}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={m.showRate >= 75 ? 'text-success' : m.showRate >= 50 ? 'text-warning' : 'text-destructive'}>
                          {m.showRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">{m.demosClosed}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={m.closeRate >= 30 ? 'text-success' : m.closeRate >= 20 ? 'text-warning' : 'text-destructive'}>
                          {m.closeRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">${m.avgDealSize.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-medium">${m.totalRevenue.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="bg-surface font-bold">
                    <td className="py-3 px-4" colSpan={2}>TOTALS</td>
                    <td className="py-3 px-4 text-center">{totals.demosBooked}</td>
                    <td className="py-3 px-4 text-center">{totals.demosShowed}</td>
                    <td className="py-3 px-4 text-center">{avgShowRate}%</td>
                    <td className="py-3 px-4 text-center">{totals.demosClosed}</td>
                    <td className="py-3 px-4 text-center">{avgCloseRate}%</td>
                    <td className="py-3 px-4 text-right">--</td>
                    <td className="py-3 px-4 text-right">${totals.totalRevenue.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Close Rate Comparison Chart */}
      {chartData.length > 0 && (
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Close Rate Comparison by Client</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                  <YAxis dataKey="name" type="category" width={120} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Close Rate']}
                  />
                  <Bar dataKey="closeRate" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-4 mt-4 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success" />
                <span className="text-muted-foreground">Excellent (&ge;30%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-secondary" />
                <span className="text-muted-foreground">Good (20-29%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-destructive" />
                <span className="text-muted-foreground">Needs Improvement (&lt;20%)</span>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>
      )}
    </div>
  );
}
